import SplineRobot from "./SplineRobot";

/**
 * CopilotHeroAsset — Rev 31 mask-based diagonal-cut pass.
 *
 * Architecture: outer relative positioner (no mask, no overflow-hidden) +
 * inner masked wrapper (mask-image diagonal cut, overflow: hidden).
 *
 * WHY the mask is on the wrapper div, not on the spline-viewer element:
 * Rev 25.4's viewer-level mask-image was unreliable in production — the
 * Spline canvas's internal shadow DOM / stacking context would intermittently
 * override or drop the mask. Applying the mask to our own wrapper div
 * (data-copilot-hero-asset) is deterministic: we own the element and its
 * stacking context, so the clip is guaranteed regardless of inner canvas
 * behaviour.
 *
 * The four previous gradient overlay divs (data-robot-cover,
 * data-robot-cover-l, data-robot-cover-r, data-robot-cut-fade) are deleted.
 * Those soft asymmetric radials read as foggy, inconsistent haze around the
 * black platform — especially on hover. A single mask-image linear-gradient
 * produces a crisp diagonal cut with strict L=R symmetry and lets the
 * paper-graph grid read cleanly.
 *
 * HUD chrome (corner brackets + status pill) live on the OUTER positioner so
 * the mask does not clip them. They paint on top via z-30 and remain after
 * the inner masked div in DOM order.
 *
 * `pointer-events: none` on every HUD span preserves Spline cursor reactivity.
 * The mask itself is render-only and does not block pointer events on the
 * Spline canvas.
 *
 * NOTE: mask angle (170deg) and stops (78/80/82%) are INITIAL values flagged
 * for screenshot calibration by the orchestrator. Do not tune here.
 *
 * Container is fixed at 480px tall on the homepage hero. The mobile branch
 * hides the entire `[data-hero-robot]` grid area via
 * `nx-hero-robot { display: none }` in index.css; this component therefore
 * only renders on lg+ from Home.tsx.
 */
export function CopilotHeroAsset() {
  return (
    /* OUTER positioner — no mask, no overflow-hidden. Carries HUD chrome. */
    <div className="relative w-full" style={{ height: "480px" }}>
      {/* INNER masked wrapper — diagonal cut mask, overflow: hidden for
          belt-and-suspenders containment. Spline scene lives here. */}
      <div
        data-copilot-hero-asset
        className="absolute inset-0"
        style={{
          borderRadius: "var(--nx-r-3)",
          overflow: "hidden",
          zIndex: 2,
          WebkitMaskImage:
            "linear-gradient(180deg, #000 0%, #000 82%, transparent 96%)",
          maskImage:
            "linear-gradient(180deg, #000 0%, #000 82%, transparent 96%)",
        }}
      >
        {/* Spline scene — fills the masked wrapper. SplineRobot owns
            lazy-mount + IntersectionObserver + 2s idle + reduced-motion
            fallback. */}
        <div className="relative h-full w-full">
          <SplineRobot />
        </div>
      </div>

      {/* HUD corner brackets — pure CSS, ~12px L-shapes at each corner.
          Mounted on the OUTER positioner so the mask does not clip them.
          z-30 paints above the masked inner div. */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-3 top-3 z-30 h-3 w-3 border-l border-t border-nx-line-strong"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute right-3 top-3 z-30 h-3 w-3 border-r border-t border-nx-line-strong"
      />
      {/* Bottom corner brackets removed (Rev 31): with the diagonal-cut mask
          they floated in the empty grid zone below the cut, reading as a UI
          glitch rather than framing the asset. Top brackets anchor the HUD. */}

      {/* Status pill — "● NEXIRT Copilot online" HUD label.
          Also on the OUTER positioner so the mask doesn't clip it. */}
      <span
        data-copilot-hero-asset-status
        className="pointer-events-none absolute right-4 top-4 z-30 inline-flex items-center gap-1.5 rounded-nx-full border border-nx-line bg-nx-bg/55 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-nx-text-dim backdrop-blur-sm"
      >
        <span
          className="inline-block h-1.5 w-1.5 rounded-full bg-nx-accent-2"
          style={{ boxShadow: "0 0 8px var(--nx-accent-2)" }}
        />
        NEXIRT Copilot online
      </span>
    </div>
  );
}

export default CopilotHeroAsset;
